import { DownloadIcon, Heading, PickupsIcon, ResolveIcon, CarrierHoverCell } from '@/components';
import {
  Copy,
  CornerDownLeft,
  Eye,
  MapPin,
  PackageCheck,
  RefreshCw,
  RotateCw,
  Snowflake,
  Undo2,
} from 'lucide-react';
import {
  CUSTOM_ID,
  CUSTOMER_NAME,
  LM_COMMENT,
  ON_HOLD_REASON,
  ORDER_ACTIONS,
  ORDER_ID,
  ORDER_STATUS,
  PICKUP_LOCATION,
  PRODUCTS,
  ROUTE,
  OrderStatusEnum,
} from '@/config';
import { ColumnsType } from 'antd/es/table';
import {
  OrderStatusTab,
  Product,
  PermissionName,
  Carrier,
  PickupDataResponse,
  PreferredTiming,
} from '@/types';
import { ActionsTable } from '@/components/Action';
import { Flex, message } from 'antd';
import { downloadFile, getOrderTabByStatus, translateCarrierMessage } from '@/utils';
import { useRequestPickup } from '@/hooksApi';
import { isSlotSelectionRequired } from '@/components/PickupSlots/slotGate';
import useSlotSelectionStore from '@/stores/useSlotSelectionStore';
import { showAutoAdvanceToastIfAny } from '@/hooks/autoAdvanceToast';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { fetchSingleLabelPickupByUID } from '@/api';
import { useState } from 'react';
import { OrderDataType, OrderStatus } from '@/hooks/useOrderData';
import { t } from 'i18next';
import { ActionProps, OrderMenuItemsEnum } from '@/components/Action/ActionsTable';
import usePermission from '@/hooks/usePermission';
import { InventoryOrder } from '@/types/inventory';

/** Callbacks for inventory-tab-specific row actions (Resend, Return Now). */
export interface InventoryCallbacks {
  onResend: (order: InventoryOrder) => void;
  onReturnNow: (order: InventoryOrder) => void;
}

const renderHeading = (text: string | undefined) => <Heading title={text} fontSize='text-xs' />;

// O-10: Use merchant_title from API (human-facing label) with slug translation as fallback.
// Bulk-collected override: when the order was collected via a BULK pickup and is
// still at the `collected` status, show "Bulk Collected" instead of "Collected".
// `bulk-collected` is NOT an order status — it's a flag on the order — so once the
// order advances past `collected` the normal label is used.
const getStatusTitle = (
  status: { merchant_title?: string; slug: string },
  isBulkCollected?: boolean,
) => {
  if (isBulkCollected && status.slug === 'collected') {
    return String(t('merchant_title_order_status.bulk-collected'));
  }
  return status.merchant_title || t(`merchant_title_order_status.${status.slug}`);
};

// Import from single source of truth — no duplicate color maps
import { STATUS_HSL } from '@/pages/order-details/components/StatusColors';

export const getColumns = (
  hasSearchValue: boolean,
  activeTab: OrderStatusTab,
  activeSubTab: string,
  t: (key: string) => string,
  // Pre-computed action column — callers should hoist getActionColumn() outside any
  // conditional so it always runs (hooks must not be called conditionally).
  actionColumn?: Record<string, unknown>,
): ColumnsType<any> => {
  const columns: any[] = [
    {
      title: String(t('order')),
      dataIndex: ORDER_ID,
      key: ORDER_ID,
      sorter: (a: { orderDateRaw: string }, b: { orderDateRaw: string }) =>
        new Date(a.orderDateRaw).getTime() - new Date(b.orderDateRaw).getTime(),
      render: (
        _: string,
        record: { orderId: string; orderDate: string; rtoRequestedAt?: string | null },
      ) => (
        <div className='flex flex-col gap-0.5'>
          <div className='flex items-center gap-1.5'>
            <span
              style={{
                fontSize: '13px',
                fontWeight: 700,
                fontFamily: 'monospace',
                color: 'rgb(57,57,60)',
              }}
            >
              {record.orderId}
            </span>
            <Copy
              size={12}
              className='cursor-pointer text-gray-400 hover:text-gray-600 shrink-0'
              onClick={() => {
                navigator.clipboard.writeText(record.orderId ?? '');
                message.success(String(t('copied')));
              }}
            />
          </div>
          <span style={{ fontSize: '11px', color: 'rgb(115,123,140)' }}>{record.orderDate}</span>
          {/* FE-2: Show RTO-requested timestamp below the created date when
              present. Only populated for orders that have ever entered an
              rto-requested / rto-request-pending status. */}
          {record.rtoRequestedAt && (
            <span
              data-testid='rto-requested-at'
              style={{ fontSize: '11px', color: 'rgb(185,28,28)' }}
            >
              {String(t('return_requested_at'))}: {record.rtoRequestedAt}
            </span>
          )}
        </div>
      ),
    },
    {
      title: String(t('status')),
      dataIndex: ORDER_STATUS,
      key: ORDER_STATUS,
      render: (status: OrderStatus, record: { is_bulk_collected?: boolean }) => {
        if (!status) return '---';
        const hsl = STATUS_HSL[status.slug] ?? { bg: 'hsla(0,0%,60%,0.10)', text: 'hsl(0,0%,38%)' };
        return (
          <span
            className='inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap'
            style={{ backgroundColor: hsl.bg, color: hsl.text }}
          >
            {getStatusTitle(status, record?.is_bulk_collected)}
          </span>
        );
      },
    },
    {
      title: String(t('carrier')),
      dataIndex: 'carrier',
      key: 'carrier',
      render: (carrier: Carrier, record: { carrierList?: Carrier[]; key: string }) => {
        return <CarrierHoverCell key={record.key} orderId={record.key} carrier={carrier} />;
      },
    },
    {
      // Item 2 — surface the shipping rule that ACTUALLY assigned this
      // order's carrier. Backend sends `applied_rule_name` only when a rule
      // produced the carrier; it is null when the merchant overrode the
      // carrier. Falls back to a dash — never a stale or fabricated name.
      title: String(t('rule_column_header')),
      dataIndex: 'applied_rule_name',
      key: 'rule',
      render: (
        _: unknown,
        record: { applied_rule_uid?: string | null; applied_rule_name?: string | null },
      ) => {
        const name = record?.applied_rule_name;
        if (!name) return <span style={{ color: 'rgb(115,123,140)' }}>---</span>;
        return (
          <span
            title={String(t('rule_applied_tooltip')).replace('{name}', name)}
            className='inline-flex items-center max-w-[160px] truncate rounded-full px-2 py-0.5 text-[11px] font-medium'
            style={{ backgroundColor: 'hsl(150,55%,94%)', color: 'hsl(150,55%,28%)' }}
          >
            {name}
          </span>
        );
      },
    },
    {
      title: String(t('order_type')),
      dataIndex: 'orderType',
      key: 'orderType',
      render: (
        _: unknown,
        record: { orderType?: string; temperature_control?: boolean },
      ) => {
        const type = record.orderType ?? 'DEFAULT';
        const typeConfig: Record<string, { labelKey: string; icon: JSX.Element }> = {
          DEFAULT: {
            labelKey: 'order_type_normal',
            icon: <PackageCheck size={12} />,
          },
          EXCHANGE: {
            labelKey: 'order_type_exchange',
            icon: <RefreshCw size={12} />,
          },
          RETURN: {
            labelKey: 'order_type_returned',
            icon: <CornerDownLeft size={12} />,
          },
        };
        const config = typeConfig[type] ?? typeConfig['DEFAULT'];
        return (
          <div className='flex items-center gap-1.5 flex-wrap'>
            <span
              className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold border border-solid bg-white'
              style={{ borderColor: 'rgb(232,230,227)' }}
            >
              {config.icon}
              {String(t(config.labelKey))}
            </span>
            {/* TEMP-9 — cold-chain pill. Rendered ONLY when the order carries
                the temperature_control flag; sits next to the order-type pill
                (same pill grammar) so shipment traits read as one group. */}
            {record.temperature_control ? (
              <span
                data-testid='temperature-control-pill'
                title={String(t('temperature_controlled'))}
                className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold border border-solid'
                style={{
                  borderColor: 'hsl(210,80%,85%)',
                  backgroundColor: 'hsl(210,90%,96%)',
                  color: 'hsl(212,70%,38%)',
                }}
              >
                <Snowflake size={12} />
                {String(t('temperature_controlled'))}
              </span>
            ) : null}
          </div>
        );
      },
    },
    {
      title: String(t('customer')),
      dataIndex: CUSTOMER_NAME,
      key: CUSTOMER_NAME,
      render: (
        _: any,
        record: { customerName: string | undefined; customerAddress: string | undefined },
      ) => (
        <div className='flex flex-col gap-0.5'>
          <span style={{ fontSize: '13px', fontWeight: 600, color: 'rgb(57,57,60)' }}>
            {record.customerName ?? '---'}
          </span>
          <span
            style={{ fontSize: '11px', color: 'rgb(115,123,140)' }}
            className='truncate max-w-[200px]'
          >
            {record.customerAddress ?? '---'}
          </span>
        </div>
      ),
    },
    {
      title: String(t('pickup_location')),
      dataIndex: PICKUP_LOCATION,
      key: PICKUP_LOCATION,
      render: (text: string) => (
        <div className='flex items-center gap-1'>
          <MapPin size={13} style={{ color: 'rgb(115,123,140)', flexShrink: 0 }} />
          <Heading title={text} fontSize='text-sm' />
        </div>
      ),
    },
    {
      title: String(t('item')),
      dataIndex: PRODUCTS,
      key: PRODUCTS,
      render: (products: Product[], record: { orderValue: number | undefined }) => {
        const firstProduct = products?.[0];
        return (
          <div className='flex flex-col gap-0.5'>
            <span
              className='max-w-[180px] truncate'
              style={{ fontSize: '12px', fontWeight: 500, color: 'rgb(57,57,60)' }}
              title={firstProduct?.name}
            >
              {firstProduct?.name ?? '---'}
            </span>
            <span style={{ fontSize: '11px', color: 'rgb(115,123,140)' }}>
              {record?.orderValue != null
                ? `${String(t('total'))} ${record.orderValue.toLocaleString('en-US')} ${String(t('iqd'))}`
                : '---'}
            </span>
          </div>
        );
      },
    },
    {
      title: String(t('filter_custom_id')),
      dataIndex: CUSTOM_ID,
      key: CUSTOM_ID,
      render: (text: string) => (
        // FE-10: Cap the custom-ID column to a sensible max-width and truncate
        // with an ellipsis. Long merchant-defined IDs (e.g. "Lili-131702-MFB")
        // were making this column overflow the table and push layout sideways.
        // Full value remains available via the tooltip and copy button.
        <div className='group flex items-center gap-1 min-w-0'>
          <span
            title={text}
            className='truncate max-w-[180px]'
            style={{ fontSize: '12px', fontFamily: 'monospace', color: 'rgb(115,123,140)' }}
          >
            {text ?? '---'}
          </span>
          {text && text !== '---' && (
            <Copy
              size={12}
              className='cursor-pointer text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0'
              onClick={() => {
                navigator.clipboard.writeText(text);
                message.success(String(t('copied')));
              }}
            />
          )}
        </div>
      ),
    },
  ];

  if (
    // (activeTab === OrderStatusTab.IN_PROGRESS && ['all', 'postponed'].includes(activeSubTab)) || // as requested in ticket BO-988
    activeTab === OrderStatusTab.ON_HOLD
  ) {
    columns.push(
      {
        title: t('reason'),
        dataIndex: ON_HOLD_REASON,
        key: ON_HOLD_REASON,
        width: 180,
        ellipsis: { showTitle: true },
        render: (text: string) => renderHeading(text),
      },
      {
        title: t('carrier_comment'),
        dataIndex: LM_COMMENT,
        key: LM_COMMENT,
        width: 180,
        ellipsis: { showTitle: true },
        render: (text: string) => renderHeading(translateCarrierMessage(text, t)),
      },
    );
  }

  // Use the pre-computed action column if provided; fall back to calling getActionColumn
  // directly only when getColumns is invoked outside the Orders component (e.g. tests).
  columns.push(actionColumn ?? getActionColumn(hasSearchValue ? undefined : activeTab));

  return columns || [];
};

const renderActionColumnByStatus = (
  orderTab: OrderStatusTab,
  data: OrderDataType,
  handleShipment: (id: string) => void,
  isShippingThisOrder: boolean,
  selectedId: string | null,
  setSelectedId: (id: string | null) => void,
  hasPermission: (permission: string) => boolean,
  platformCode?: string,
  inventoryCallbacks?: InventoryCallbacks,
  navigateFn?: (path: string) => void,
) => {
  const disableOnHold = (status?: string) => {
    return ['hold-resolved', 'on-hold', 'exception'].includes(status || '');
  };

  const handleDownloadLabel = async () => {
    setSelectedId(data.key);
    await downloadFile(
      data.key as string,
      fetchSingleLabelPickupByUID,
      `${data?.platform_code}.pdf`,
    );
    setSelectedId(null);
  };

  const commonMenuItems = [OrderMenuItemsEnum.DUPLICATE_ORDER, OrderMenuItemsEnum.CREATE_TICKET];

  const commonMenuWithoutDuplicate = [OrderMenuItemsEnum.CREATE_TICKET];

  const inProgressActionsByStatus: Partial<Record<OrderStatusEnum, OrderMenuItemsEnum[]>> = {
    // Bug 32: Edit restricted to scheduled only (new is in READY_TO_SHIP tab)
    // Bug 23-24: Cancel restricted to scheduled, collecting, collected only
    [OrderStatusEnum.scheduled]: [
      OrderMenuItemsEnum.EDIT_ORDER,
      ...commonMenuItems,
      OrderMenuItemsEnum.CANCEL_ORDER,
    ],
    [OrderStatusEnum.out_for_collecting]: [...commonMenuItems],
    [OrderStatusEnum.collecting]: [...commonMenuItems, OrderMenuItemsEnum.CANCEL_ORDER],
    [OrderStatusEnum.collected]: [...commonMenuItems, OrderMenuItemsEnum.CANCEL_ORDER],
    [OrderStatusEnum.sorted]: [...commonMenuItems],
    [OrderStatusEnum.received_warehouse]: [...commonMenuItems],
    [OrderStatusEnum.in_transit]: [...commonMenuItems],
  };

  const actionTableProps = {
    uid: data.key,
    activeTab: orderTab,
    orderData: data,
    savePrevPage: true,
  };

  /** Reconstruct a minimal InventoryOrder from the shared OrderDataType so the
   *  Resend / Return-Now flows receive the shape they expect. */
  const buildInventoryOrder = (d: OrderDataType): InventoryOrder => ({
    uid: d.key,
    platform_code: d.platform_code,
    full_name: d.customerName ?? '',
    phone: d.phone ?? '',
    address_text: d.customerAddress ?? '',
    region_id: 0,
    city_id: 0,
    size: '',
    type: '',
    is_fragile: false,
    fee: typeof d.orderValue === 'number' ? d.orderValue : 0,
    products: (d.products ?? []).map((p) => ({
      title: p.name ?? '',
      quantity: typeof p.quantity === 'number' ? p.quantity : Number(p.quantity ?? 0),
      price: typeof p.price === 'number' ? p.price : Number(p.price ?? 0),
    })),
    parent_order_id: null,
    parent_order_uid: null,
    recommended_carrier_slug: null,
    available_carriers_detail: [],
    created_at: d.orderDate,
    merchant_id: 0,
  });

  const labelButton = {
    label: data.label_printed ? t('reprint') : t('label'),
    onClick: handleDownloadLabel,
    icon: <DownloadIcon />,
    disabled: selectedId === data.key,
    variant: (data.label_printed ? 'muted' : 'primary') as 'primary' | 'muted',
  };

  const tableActionsByTab: { [key in OrderStatusTab]: ActionProps } = {
    [OrderStatusTab.READY_TO_SHIP]: {
      button:
        data.status.slug === OrderStatusEnum.scheduled
          ? labelButton
          : {
              label: t('ship_now'),
              onClick: () => handleShipment(data.key),
              icon: <PickupsIcon />,
              isLoading: isShippingThisOrder,
              disabled:
                isShippingThisOrder ||
                !data.pickupAddressId ||
                !hasPermission(PermissionName.REQUEST_PICK_UP),
            },
      cancelLabel: data.status.slug === OrderStatusEnum.new ? t('delete') : undefined,
      menuItems: [
        ...commonMenuItems,
        OrderMenuItemsEnum.EDIT_ORDER,
        OrderMenuItemsEnum.CANCEL_ORDER,
      ],
    },
    [OrderStatusTab.IN_PROGRESS]: {
      menuItems: inProgressActionsByStatus[data.status.slug as OrderStatusEnum] ?? commonMenuItems,
      button: labelButton,
    },
    [OrderStatusTab.ON_HOLD]: {
      menuItems: disableOnHold(data?.status.slug)
        ? commonMenuItems
        : [...commonMenuItems, OrderMenuItemsEnum.EDIT_ORDER, OrderMenuItemsEnum.CANCEL_ORDER],
      // Bug 29: Resolve only when action_required=true AND status=on-hold (not retry-request)
      button:
        data.merchant_action_required && data.status.slug === OrderStatusEnum.on_hold
          ? {
              label: t('resolve'),
              onClick: 'openResolveModal',
              icon: <ResolveIcon />,
            }
          : undefined,
    },
    [OrderStatusTab.RTO]: {
      menuItems: [...commonMenuItems],
      // button: {
      //   navigateLink: `${ROUTE.ORDER_DETAILS}/${data?.key}`,
      //   icon: <EyeIcon />,
      // },
    },
    [OrderStatusTab.FINISHED]: {
      menuItems: [...commonMenuItems],
      // button: {
      //   navigateLink: `${ROUTE.ORDER_DETAILS}/${data?.key}`,
      //   icon: <EyeIcon />,
      // },
    },
    [OrderStatusTab.INVENTORY]: {
      menuItems: [OrderMenuItemsEnum.DUPLICATE_ORDER, OrderMenuItemsEnum.CREATE_TICKET],
      customMenuItems: [
        {
          key: 'view',
          label: t('view_order'),
          leadingIcon: <Eye size={16} color='#939496' />,
          onClick: () => navigateFn?.(`${ROUTE.ORDER_DETAILS}/${data.key}`),
        },
      ],
    },
  };

  // Inventory tab: render Resend + Return Now as main buttons alongside the dropdown
  // Uses Ant Design <Button type="primary"> for Resend (yellow bg + dark text, matching Ship Now)
  // and <Button danger> for Return Now (matching existing danger button pattern)
  if (
    orderTab === OrderStatusTab.INVENTORY &&
    inventoryCallbacks &&
    hasPermission(PermissionName.RESOLVE_OR_REQUEST_RTO)
  ) {
    return (
      <div className='flex items-center gap-2'>
        <button
          type='button'
          className='flex items-center gap-1.5 h-7 px-3 rounded-lg bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-[11px] font-bold uppercase cursor-pointer hover:bg-[rgb(230,217,60)] transition-colors whitespace-nowrap'
          onClick={() => inventoryCallbacks.onResend(buildInventoryOrder(data))}
        >
          <RotateCw size={12} />
          {t('resend_action')}
        </button>
        <button
          type='button'
          className='flex items-center gap-1.5 h-7 px-3 rounded-lg bg-[rgb(239,68,68)] text-white text-[11px] font-bold uppercase cursor-pointer hover:bg-[rgb(220,38,38)] transition-colors whitespace-nowrap'
          onClick={() => inventoryCallbacks.onReturnNow(buildInventoryOrder(data))}
        >
          <Undo2 size={12} />
          {t('return_now')}
        </button>
        <ActionsTable
          wrapperJustify='end'
          {...actionTableProps}
          {...tableActionsByTab[orderTab]}
          ticketEntityPlatformCode={platformCode}
        />
      </div>
    );
  }

  return (
    <ActionsTable
      wrapperJustify='end'
      {...actionTableProps}
      {...tableActionsByTab[orderTab]}
      ticketEntityPlatformCode={platformCode}
    />
  );
};

export const getActionColumn = (
  selectedTab?: OrderStatusTab,
  inventoryCallbacks?: InventoryCallbacks,
) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [shippingIds, setShippingIds] = useState<Set<string>>(() => new Set());

  const { mutateAsync: requestPickup } = useRequestPickup();
  const { hasPermission } = usePermission();
  const { openSlotSelection } = useSlotSelectionStore();

  const queryClient = useQueryClient();
  const navigation = useNavigate();

  const handleShipment = async (id: string) => {
    setShippingIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });

    // v2-task-12fix (H2): this is the flow the QA report's live capture of
    // `POST /merchants/pick-ups/request` with `auto_advanced`/`slot` on the
    // response actually exercised — the Orders-list per-row "Ship Now"
    // button, wired straight to the SINGLE-pickup endpoint via
    // `useRequestPickup` (NOT the bulk-select action bar, which posts to
    // `/merchants/pick-ups/bulk-request` — an async, fire-and-forget Job
    // that never returns a stamped pick-up; see useSubmitOrders.tsx). The
    // response is now captured and passed through the shared
    // showAutoAdvanceToastIfAny helper (same logic as
    // useSubmitSingleOrder.tsx) so this surface finally shows the toast too.
    const onShipSuccess = (response?: PickupDataResponse) => {
      message.success(t('operation'));
      queryClient.refetchQueries(['orders-ready_to_ship']);
      queryClient.invalidateQueries(['order-count']);
      navigation(ROUTE.PICKUPS);
      showAutoAdvanceToastIfAny(response, t);
    };

    try {
      const response = await requestPickup({
        order_uids: [id],
      });
      onShipSuccess(response);
    } catch (error) {
      if (isSlotSelectionRequired(error)) {
        openSlotSelection({
          onSelect: async (timeSlotCode: string, preferredTiming?: PreferredTiming | null) => {
            try {
              const response = await requestPickup({
                order_uids: [id],
                time_slot_code: timeSlotCode,
                ...(preferredTiming !== undefined ? { preferred_timing: preferredTiming } : {}),
              });
              onShipSuccess(response);
            } catch (retryError) {
              if (isSlotSelectionRequired(retryError)) {
                console.error(retryError);
              }
            }
          },
        });
        return;
      }
      throw error;
    } finally {
      setShippingIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  return {
    title: t('actions'),
    key: ORDER_ACTIONS,
    render: (data: OrderDataType) => {
      const orderTab =
        selectedTab ?? (getOrderTabByStatus(data.status.slug) || OrderStatusTab.READY_TO_SHIP);

      const isShippingThisOrder = shippingIds.has(data.key);

      return renderActionColumnByStatus(
        orderTab,
        data,
        handleShipment,
        isShippingThisOrder,
        selectedId,
        setSelectedId,
        hasPermission,
        data.platform_code,
        inventoryCallbacks,
        navigation,
      );
    },
    width: '5%',
    fixed: 'right' as 'right' | undefined,
  };
};
